import { useEffect, useRef, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { toast } from "sonner";
import { useQueryClient } from "@tanstack/react-query";
import type { EmailOtpType } from "@supabase/supabase-js";
import { getSupabase } from "@/lib/supabase";
import { runAfterAuth } from "./afterAuth";

const EMAIL_OTP_TYPES: readonly EmailOtpType[] = [
  "email",
  "magiclink",
  "signup",
  "invite",
  "recovery",
  "email_change",
];

function isEmailOtpType(v: string | null): v is EmailOtpType {
  return v !== null && (EMAIL_OTP_TYPES as readonly string[]).includes(v);
}

/**
 * Landing route for magic links and web OAuth (`/auth/callback`).
 *
 * Supported URL shapes:
 *  - `?code=…` (PKCE): supabase-js exchanges it during client
 *    initialisation (`detectSessionInUrl`); `getSession()` awaits that.
 *    A code routed in later by a native deep link is exchanged here.
 *  - `?token_hash=…&type=email` (recommended e-mail template): verified
 *    here with `verifyOtp`. Needs no PKCE verifier, so it works when the
 *    link is opened on another device / browser (H4).
 *  - `?error=…&error_description=…` (or in the hash): expired or invalid
 *    link — shown to the user instead of silently bouncing to /login.
 */
export function AuthCallback() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const location = useLocation();
  const [error, setError] = useState<string | null>(null);
  const handledHref = useRef<string | null>(null);

  useEffect(() => {
    // Handle each callback URL once: StrictMode runs effects twice and a
    // token_hash / code is single-use. A *new* link routed in while this
    // page is already shown (e.g. a fresh magic link tapped after an
    // expired one, via a native deep link) changes the URL and is handled.
    const href = window.location.href;
    if (handledHref.current === href) return;
    handledHref.current = href;
    setError(null);
    // A newer link superseded this one: drop this run's outcome.
    const stale = () => handledHref.current !== href;
    const fail = () => {
      if (!stale()) setError(t("auth.callback.link_invalid"));
    };

    void (async () => {
      const url = new URL(href);
      const query = url.searchParams;
      const hash = new URLSearchParams(url.hash.replace(/^#/, ""));

      if (
        query.get("error") ||
        query.get("error_description") ||
        hash.get("error") ||
        hash.get("error_description")
      ) {
        fail();
        return;
      }

      const supabase = getSupabase();
      let userId: string;
      try {
        const tokenHash = query.get("token_hash");
        const type = query.get("type");
        if (tokenHash && isEmailOtpType(type)) {
          const { error: verifyError } = await supabase.auth.verifyOtp({
            token_hash: tokenHash,
            type,
          });
          if (verifyError) throw verifyError;
        }

        let { data, error: sessionError } = await supabase.auth.getSession();
        // supabase-js only exchanges the `?code=` present when the client
        // starts. A native deep link routed into the running app (see
        // deepLinks.ts) arrives later, so exchange it here.
        const code = query.get("code");
        if (!sessionError && !data.session && code) {
          const exchanged = await supabase.auth.exchangeCodeForSession(code);
          if (exchanged.error) throw exchanged.error;
          ({ data, error: sessionError } = await supabase.auth.getSession());
        }
        if (sessionError || !data.session) {
          throw sessionError ?? new Error("no session");
        }
        userId = data.session.user.id;
      } catch {
        fail();
        return;
      }

      if (stale()) return;
      const result = await runAfterAuth({ userId, queryClient });
      if (result.onboardingSyncFailed) {
        toast.error(t("auth.onboarding_sync_failed"));
      }
      navigate(result.destination, { replace: true });
    })();
    // location.key changes on every navigation, even to the same path.
  }, [location.key, navigate, queryClient, t]);

  if (!error) {
    return (
      <div className="grid h-full place-items-center text-muted-foreground">
        {t("auth.callback.loading")}
      </div>
    );
  }

  return (
    <div className="grid h-full place-items-center gap-4 p-8 text-center">
      <p className="text-destructive" role="alert">
        {error}
      </p>
      <button
        type="button"
        onClick={() => navigate("/login", { replace: true })}
        className="font-serif italic text-accent underline underline-offset-4"
      >
        {t("auth.callback.back_to_login")}
      </button>
    </div>
  );
}
